import _ from "lodash";
import {
  getUpdateData,
  persistAddress,
  persistCourt,
  persistPerson,
  sanitizeOrganizationEmailData,
  trimValueIfSet,
} from "./persisterService";
import { apiPatch } from "../Api/apiCall";
import ApiClient from "../ApiClient";
import { Organization } from "../../types/Organization";
import { persistAbstractCaseSubEntities } from "./AbstractCasePersister";
import { UseForm } from "../../types/UseForm";
import { Settlement } from "../../types/Products/EmploymentLaw/Settlement";

export const persistSettlement = async (updateData: UseForm["values"], origSettlement: Settlement) => {
  await persistAbstractCaseSubEntities(updateData, origSettlement);

  if (_.isObject(updateData.opponentLawyer)) {
    const opponentLawyerUpdateData = getUpdateData(updateData.opponentLawyer, origSettlement, "opponentLawyer");
    await persistPerson(origSettlement.opponentLawyer.id, opponentLawyerUpdateData);
  }

  if (_.isObject(updateData.opponentLawFirm?.organization)) {
    const opponentLawFirmUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization,
      origSettlement,
      "opponentLawFirm.organization"
    );
    await apiPatch("organizations", origSettlement.opponentLawFirm.organization.id, opponentLawFirmUpdateData);
  }

  if (_.isObject(updateData.opponentLawFirm?.organization.mainEmail)) {
    const opponentLawFirmEmailUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization.mainEmail,
      origSettlement,
      "opponentLawFirm.organization.mainEmail"
    );
    await ApiClient.patch(`organization_emails/${origSettlement.opponentLawFirm.organization.mainEmail.id}`, {
      body: JSON.stringify(sanitizeOrganizationEmailData(opponentLawFirmEmailUpdateData)),
    });
  }

  if (
    _.has(updateData, "opponentLawFirm.organization.address") &&
    _.isObject(updateData.opponentLawFirm.organization.address)
  ) {
    const opponentLawFirmAddressUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization.address,
      origSettlement,
      "opponentLawFirm.organization.address"
    );
    await persistAddress(origSettlement.opponentLawFirm.organization.address.id, opponentLawFirmAddressUpdateData);
  }

  if (_.has(updateData, "responsibleCourt") && _.isObject(updateData.responsibleCourt)) {
    const courtUpdateData = getUpdateData(updateData.responsibleCourt, origSettlement, "responsibleCourt");
    !_.isEmpty(courtUpdateData) && (await persistCourt(origSettlement.responsibleCourt.id, courtUpdateData));
  }

  if (_.has(updateData, "petitioner.residenceAddress") && _.isObject(updateData.petitioner.residenceAddress)) {
    const petitionerAddressUpdateData = getUpdateData(
      updateData.petitioner.residenceAddress,
      origSettlement,
      "petitioner.residenceAddress"
    );
    await persistAddress(origSettlement.petitioner.residenceAddress.id, petitionerAddressUpdateData);
  }

  if (_.isObject(updateData.employerOrganization)) {
    const employerUpdateData = getUpdateData(updateData.employerOrganization, origSettlement, "employerOrganization");
    await ApiClient.patch(`organizations/${origSettlement.employerOrganization.id}`, {
      body: JSON.stringify(sanitizeEmployerData(employerUpdateData)),
    });
  }

  if (_.has(updateData, "employerOrganization.mainEmail") && _.isObject(updateData.employerOrganization.mainEmail)) {
    const employerEmailUpdateData = getUpdateData(
      updateData.employerOrganization.mainEmail,
      origSettlement,
      "employerOrganization.mainEmail"
    );
    await apiPatch(
      "organization_emails",
      origSettlement.employerOrganization.mainEmail.id,
      sanitizeOrganizationEmailData(employerEmailUpdateData)
    );
  }

  if (_.has(updateData, "employerOrganization.address") && _.isObject(updateData.employerOrganization.address)) {
    const employerAddressUpdateData = getUpdateData(
      updateData.employerOrganization.address,
      origSettlement,
      "employerOrganization.address"
    );
    await persistAddress(origSettlement.employerOrganization.address.id, employerAddressUpdateData);
  }

  let settlementUpdateData = getUpdateData(updateData, origSettlement);
  if (updateData.terminationObstacles) {
    settlementUpdateData["terminationObstacles"] = updateData.terminationObstacles;
  }

  await apiPatch("settlements", origSettlement.id, settlementUpdateData);
};

const sanitizeEmployerData = (employerData: Partial<Organization>) => {
  let sanitizedEmployerData = _.cloneDeep(employerData);
  sanitizedEmployerData = trimValueIfSet("name", sanitizedEmployerData);
  sanitizedEmployerData = trimValueIfSet("address.streetAddress", sanitizedEmployerData);
  sanitizedEmployerData = trimValueIfSet("postalCode", sanitizedEmployerData);
  sanitizedEmployerData = trimValueIfSet("addressLocality", sanitizedEmployerData);
  return sanitizedEmployerData;
};
